import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import usePwaManifest from '../utils/usePwaManifest';

export default function TenantLogin() {
  usePwaManifest('/manifest-tenant.json');
  const navigate = useNavigate();
  useEffect(() => { try { if (new URLSearchParams(window.location.search).get('reply')) sessionStorage.setItem('pendingReply', '1'); } catch {} }, []);
  const [phone, setPhone] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [resolvedPhone, setResolvedPhone] = useState('');
  const [step, setStep] = useState(1);
  const [demoCode, setDemoCode] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    if (step !== 2) return;
    const t = setInterval(() => setSecondsLeft(s => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [step]);

  async function handleSendOtp(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const r = await api.post('/portal/tenant-otp/send', { phone, idNumber });
      setDemoCode(r.data.demo_code);
      setTenantName(r.data.name || '');
      setResolvedPhone(r.data.resolvedPhone || phone.replace(/[\s\-]/g, ''));
      setStep(2);
      setSecondsLeft(5 * 60);
      // Web OTP API — Android Chrome autofill
      if ('OTPCredential' in window) {
        const ac = new AbortController();
        navigator.credentials.get({ otp: { transport: ['sms'] }, signal: ac.signal })
          .then(cred => {
            if (!cred?.code) return;
            const digits = cred.code.slice(0, 4).split('');
            setOtp(digits);
            setTimeout(() => {
              setError(''); setLoading(true);
              doLogin(cred.code.slice(0, 4)).catch(err => {
                setError(err.response?.data?.error || 'קוד שגוי');
                setOtp(['', '', '', '']);
                setLoading(false);
              });
            }, 100);
          })
          .catch(() => {});
      }
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה');
    } finally { setLoading(false); }
  }

  async function doLogin(code) {
    const resolvedP = resolvedPhone || phone.replace(/[\s\-]/g, '');
    const vr = await api.post('/portal/tenant-otp/verify', { phone: resolvedP, code });
    localStorage.removeItem('tenantIsAdminPreview'); // a real login is never the admin's "view as tenant" preview
    if (vr.data.token) localStorage.setItem('tenantToken', vr.data.token);
    const r = await api.get(`/portal/tenant-project?phone=${encodeURIComponent(resolvedP)}&login=1&idNumber=${encodeURIComponent(idNumber)}`);
    localStorage.setItem('tenantPhone', resolvedP);
    localStorage.setItem('tenantProjects', JSON.stringify(r.data.projects || []));
    localStorage.setItem('tenantName', r.data.name || '');
    localStorage.setItem('tenantBuildingAddress', r.data.buildingAddress || '');
    // the SMS link (?reply=1): show the manager's answers first
    if (sessionStorage.getItem('pendingReply') === '1') { sessionStorage.removeItem('pendingReply'); navigate('/portal-replies'); return; }
    navigate('/tenant-projects');
  }

  async function handleVerify() {
    const code = otp.join('');
    if (code.length < 4) return;
    setError(''); setLoading(true);
    try {
      await doLogin(code);
    } catch (err) {
      setError(err.response?.data?.error || 'קוד שגוי');
      setOtp(['', '', '', '']);
      inputRefs[0].current?.focus();
    } finally { setLoading(false); }
  }

  function handleOtpChange(i, val) {
    if (!/^\d*$/.test(val)) return;
    // iOS's SMS-code autofill suggestion doesn't fire a paste event — it sets the
    // focused box's value directly to the full code (e.g. "1234"), which the old
    // `val.slice(-1)` reduced to just its last digit. Treat any multi-digit value
    // here the same as a paste: spread it across the remaining boxes.
    if (val.length > 1) {
      const digits = val.slice(0, 4).split('');
      const next = ['', '', '', ''];
      digits.forEach((d, idx) => { next[idx] = d; });
      setOtp(next);
      if (next.every(d => d) && next.join('').length === 4) {
        setTimeout(() => {
          const code = next.join('');
          setError(''); setLoading(true);
          doLogin(code).catch(err => {
            setError(err.response?.data?.error || 'קוד שגוי');
            setOtp(['', '', '', '']);
            inputRefs[0].current?.focus();
            setLoading(false);
          });
        }, 100);
      } else {
        inputRefs[digits.length]?.current?.focus();
      }
      return;
    }
    const next = [...otp];
    next[i] = val.slice(-1);
    setOtp(next);
    if (val && i < 3) inputRefs[i + 1].current?.focus();
    if (next.every(d => d) && next.join('').length === 4) {
      setTimeout(() => {
        const code = next.join('');
        setError(''); setLoading(true);
        doLogin(code).catch(err => {
          setError(err.response?.data?.error || 'קוד שגוי');
          setOtp(['', '', '', '']);
          inputRefs[0].current?.focus();
          setLoading(false);
        });
      }, 100);
    }
  }

  function handleOtpKey(i, e) {
    if (e.key === 'Backspace' && !otp[i] && i > 0) inputRefs[i - 1].current?.focus();
  }

  function handleOtpPaste(e) {
    e.preventDefault();
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4).split('');
    if (!digits.length) return;
    const next = ['', '', '', ''];
    digits.forEach((d, i) => { next[i] = d; });
    setOtp(next);
    const lastFilled = Math.min(digits.length, 3);
    inputRefs[lastFilled].current?.focus();
    if (digits.length === 4) {
      setTimeout(() => {
        setError(''); setLoading(true);
        doLogin(digits.join('')).catch(err => {
          setError(err.response?.data?.error || 'קוד שגוי');
          setOtp(['', '', '', '']);
          setLoading(false);
        });
      }, 100);
    }
  }

  const boxStyle = { background: 'white', borderRadius: 16, padding: 40, width: '100%', maxWidth: 400, boxShadow: '0 4px 24px rgba(0,0,0,0.10)' };

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', direction: 'rtl' }}>
      <div style={boxStyle}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/logos/mabat_white_600.png" alt="MABAT" width={300} height={164} style={{ width: 300, height: 'auto', aspectRatio: '600 / 327', marginBottom: 8, objectFit: 'contain' }} />
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1f2937', margin: 0 }}>פורטל דיירים</h1>
          <p style={{ color: '#6b7280', marginTop: 8 }}>
            {step === 1 ? 'הזן טלפון ותעודת זהות' : `קוד נשלח ל-${resolvedPhone}`}
          </p>
        </div>

        {step === 1 && (
          <form onSubmit={handleSendOtp}>
            <input
              type="text" inputMode="numeric" value={idNumber}
              onChange={e => setIdNumber(e.target.value)}
              placeholder="מספר תעודת זהות"
              style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1px solid #d1d5db', fontSize: '1rem', direction: 'ltr', textAlign: 'center', marginBottom: 10, boxSizing: 'border-box' }}
            />
            <input
              type="tel" inputMode="numeric" value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="מספר טלפון"
              style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1px solid #d1d5db', fontSize: '1rem', direction: 'ltr', textAlign: 'center', marginBottom: 16, boxSizing: 'border-box' }}
            />
            {error && <div style={{ color: '#dc2626', textAlign: 'center', marginBottom: 12, fontSize: '0.9rem' }}>{error}</div>}
            <button type="submit" disabled={loading || !phone || !idNumber}
              style={{ width: '100%', padding: '12px', borderRadius: 10, background: '#1a4a8a', color: 'white', border: 'none', fontSize: '1rem', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'שולח...' : 'שלח קוד'}
            </button>
          </form>
        )}

        {step === 2 && (
          <div>
            {/* Demo SMS box - shown only in demo mode */}
            {demoCode && (
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '14px 18px', marginBottom: 20, textAlign: 'center' }}>
              <div style={{ fontSize: '0.78rem', color: '#1d4ed8', fontWeight: 600, marginBottom: 4 }}>📱 הודעת SMS (דמו)</div>
              <div style={{ fontSize: '0.85rem', color: '#374151', marginBottom: 6 }}>
                שלום {tenantName}, קוד האימות שלך לפורטל הדיירים:
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 800, color: '#1a4a8a', letterSpacing: 8, direction: 'ltr' }}>{demoCode}</div>
            </div>
            )}

            {/* OTP inputs */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 20, direction: 'ltr' }}>
              {otp.map((d, i) => (
                <input key={i} ref={inputRefs[i]}
                  type="text" inputMode="numeric" value={d}
                  autoComplete="one-time-code"
                  onChange={e => handleOtpChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKey(i, e)}
                  onPaste={handleOtpPaste}
                  style={{ width: 56, height: 64, textAlign: 'center', fontSize: '1.8rem', fontWeight: 700, border: '2px solid #d1d5db', borderRadius: 10, outline: 'none' }}
                  onFocus={e => e.target.style.borderColor = '#1a4a8a'}
                  onBlur={e => e.target.style.borderColor = '#d1d5db'}
                />
              ))}
            </div>

            {/* Countdown / expiry + resend */}
            <div style={{ textAlign: 'center', marginBottom: 14, fontSize: '0.85rem' }}>
              {secondsLeft > 0 ? (
                <span style={{ color: '#6b7280' }}>
                  הקוד תקף עוד {String(Math.floor(secondsLeft / 60)).padStart(2, '0')}:{String(secondsLeft % 60).padStart(2, '0')} דקות
                </span>
              ) : (
                <span style={{ color: '#dc2626', fontWeight: 600 }}>הקוד פג תוקף</span>
              )}
              {' '}
              <button type="button" onClick={handleSendOtp} disabled={secondsLeft > 0 || loading}
                style={{ background: 'none', border: 'none', color: secondsLeft > 0 ? '#9ca3af' : '#1a4a8a', fontWeight: 600, cursor: secondsLeft > 0 ? 'default' : 'pointer', textDecoration: secondsLeft > 0 ? 'none' : 'underline', fontSize: '0.85rem', padding: 0 }}>
                שלח קוד שוב
              </button>
            </div>

            {error && <div style={{ color: '#dc2626', textAlign: 'center', marginBottom: 12, fontSize: '0.9rem' }}>{error}</div>}

            <button onClick={handleVerify} disabled={loading || otp.join('').length < 4}
              style={{ width: '100%', padding: '12px', borderRadius: 10, background: '#1a4a8a', color: 'white', border: 'none', fontSize: '1rem', fontWeight: 700, cursor: 'pointer', marginBottom: 10, opacity: loading ? 0.7 : 1 }}>
              {loading ? 'מאמת...' : 'כניסה'}
            </button>
            <button onClick={() => { setStep(1); setOtp(['','','','']); setError(''); setPhone(''); setIdNumber(''); }}
              style={{ width: '100%', padding: '10px', borderRadius: 10, background: 'none', border: '1px solid #d1d5db', color: '#6b7280', fontSize: '0.9rem', cursor: 'pointer' }}>
              חזרה
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
